import React, {useMemo} from 'react';
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildCountryPath,
    buildHotspotDetailPath,
    buildHotspotExplorerSearch,
    getCachedHotspotExplorerStats,
    getCachedSortedHotspots,
    getHotspotExplorerHeroImage,
    getHotspotExplorerPage,
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";
import {usePageTitle} from "../../../helpers/usePageTitle";

export function AllHotspotsView({
    hotspotExplorerIndex,
    lifeListLength,
    homeLocation,
    selectedYear,
    selectedMonth,
    yearOptions,
    onChangePeriod
}: HotspotExplorerProps) {
    const resolvedHotspotExplorerIndex = hotspotExplorerIndex!;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const currentPage = getHotspotExplorerPage(searchParams);
    const hotspots = resolvedHotspotExplorerIndex.hotspots;
    const visibleHotspots = useMemo(() => getCachedSortedHotspots(hotspots, sortMode), [hotspots, sortMode]);
    const countryGroups = resolvedHotspotExplorerIndex.countryGroups;

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    };

    const setPage = (page: number) => {
        const nextSearch = buildHotspotExplorerSearch(searchParams, {page});
        navigate(`${location.pathname}${nextSearch}`, {replace: true});
    };

    const openCountry = (countryCode: string) => {
        navigate(`${buildCountryPath(countryCode)}${buildHotspotExplorerSearch(searchParams, {page: 1})}`);
    };

    const openHotspot = (locationId: string) => {
        navigate(`${buildHotspotDetailPath(locationId)}${buildHotspotExplorerSearch(searchParams, {page: 1})}`, {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    };

    const stats = useMemo(() => {
        return {
            ...getCachedHotspotExplorerStats(visibleHotspots, 'Countries'),
            speciesSeen: lifeListLength
        };
    }, [lifeListLength, visibleHotspots]);

    usePageTitle('Hotspot Explorer');

    return (
        <HotspotExplorerShell
            backgroundImage={getHotspotExplorerHeroImage({routeLevel: 'landing', homeLocation})}
            eyebrow="Hotspot Explorer"
            title="See Where You've Been"
            intro="Browse the hotspots you have visited and drill down by country and region."
            stats={stats}
            showYearsExploring={false}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            yearOptions={yearOptions}
            onChangePeriod={onChangePeriod}
            panelHeading="By Country"
            groups={countryGroups}
            onSelectGroup={openCountry}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            currentPage={currentPage}
            onPageChange={setPage}
            onOpenHotspot={openHotspot}
        />
    );
}
